import { useEffect, useRef } from "react";

/**
 * Adds an `is-visible` class to elements with the `reveal` class
 * when they enter the viewport. Re-observes any nodes added later
 * (e.g., after route changes or filter updates).
 */
export const useScrollReveal = () => {
    const observerRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.05, rootMargin: "0px 0px -20px 0px" }
        );
        observerRef.current = observer;

        const scan = () => {
            document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => observer.observe(el));
        };

        // Initial scan after layout
        const rafId = window.requestAnimationFrame(scan);

        // Re-scan periodically as a safety net for dynamic content
        const intervalId = window.setInterval(scan, 800);

        // Failsafe: any reveal element still hidden after 2.5s becomes visible
        const failsafeId = window.setTimeout(() => {
            document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => el.classList.add("is-visible"));
        }, 2500);

        // Watch for new reveal nodes added to the DOM
        const mutation = new MutationObserver(scan);
        mutation.observe(document.body, { childList: true, subtree: true });

        return () => {
            window.cancelAnimationFrame(rafId);
            window.clearInterval(intervalId);
            window.clearTimeout(failsafeId);
            mutation.disconnect();
            observer.disconnect();
        };
    }, []);
};

export default useScrollReveal;
